import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import { StatusPill } from './AdminV2Dashboard';
import { useAuth } from '../contexts/AuthContext';
import { Briefcase, ArrowLeft, Archive } from 'lucide-react';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatCard = ({ label, value, accent }) => (
  <div className="pv2-card" style={{ padding: '16px 18px', flex: '1 1 150px', minWidth: 140 }}>
    <div className="pv2-mono-sm" style={{ color: 'var(--pv2-text-subtle)' }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: accent || '#fff' }}>{value}</div>
  </div>
);

const AdminV2ServerJobs = () => {
  const { serverName: raw } = useParams();
  const serverName = decodeURIComponent(raw || '');
  const { authFetch } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ page: 1, limit: 500, archived: 'all', server: serverName });
        const res = await authFetch(`/api/admin/jobs?${params}`);
        if (!active) return;
        if (res.ok) {
          const d = await res.json();
          setJobs(d.jobs || []);
        }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [authFetch, serverName]);

  const isServed = (j) => (j.service_status || j.job_status || '').toLowerCase().includes('served')
    && !(j.service_status || '').toLowerCase().includes('non');
  const isArchived = (j) => !!j.archived_at;
  const activeJobs = jobs.filter(j => !isArchived(j));
  const served = jobs.filter(isServed).length;
  const inProgress = activeJobs.filter(j => !isServed(j)).length;
  const archived = jobs.filter(isArchived).length;

  return (
    <PortalV2Layout
      eyebrow="Operations"
      title={serverName || 'Server'}
      subtitle={`${jobs.length.toLocaleString()} job${jobs.length === 1 ? '' : 's'} assigned to this server`}
      crumbs={<><Link to="/portal/admin/jobs" style={{ opacity: 0.6 }}>Admin / Jobs</Link> <strong style={{ marginLeft: 6 }}>{serverName}</strong></>}
      action={
        <Link to="/portal/admin/jobs" className="pv2-btn pv2-btn-ghost" data-testid="pv2-server-back">
          <ArrowLeft size={14} strokeWidth={1.5} /> All Jobs
        </Link>
      }
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Jobs" value={jobs.length} />
        <StatCard label="In Progress" value={inProgress} accent="var(--pv2-red-bright)" />
        <StatCard label="Served" value={served} accent="#4ade80" />
        <StatCard label="Archived" value={archived} accent="var(--pv2-text-muted)" />
      </div>

      <div className="pv2-table-wrap">
        <table className="pv2-table" data-testid="pv2-server-jobs-table">
          <thead>
            <tr>
              <th>Job #</th>
              <th>Recipient</th>
              <th>Client</th>
              <th>Case #</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="pv2-table-empty">Loading…</td></tr>}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={6} className="pv2-table-empty">
                <Briefcase size={24} style={{ opacity: 0.3, marginBottom: 8 }} />
                <div>No jobs are currently assigned to {serverName}.</div>
              </td></tr>
            )}
            {!loading && jobs.map(j => {
              const addr = j.service_address || j.address || {};
              const cityState = [addr.city, addr.state].filter(Boolean).join(', ');
              return (
                <tr key={j.id || j.servemanager_job_id}>
                  <td>
                    <Link to={`/portal/jobs/${j.id || j.servemanager_job_id}`} className="pv2-table-job-num">{j.servemanager_job_number || '—'}</Link>
                  </td>
                  <td style={{ maxWidth: 260 }}>
                    <div style={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.recipient_name || ''}>{j.recipient_name || '—'}</div>
                    {cityState && <div style={{ fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2 }}>{cityState}</div>}
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.client_company_name || ''}>{j.client_company_name || j.client_company || '—'}</div>
                  </td>
                  <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12, color: 'var(--pv2-text-muted)' }}>{j.case_number || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <StatusPill status={j.service_status || j.job_status} />
                    {j.archived_at && <span title="Archived" style={{ marginLeft: 8, color: 'var(--pv2-text-subtle)', display: 'inline-flex', verticalAlign: 'middle' }}><Archive size={13} /></span>}
                  </td>
                  <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(j.created_at || j.received_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PortalV2Layout>
  );
};

export default AdminV2ServerJobs;
